<template>
  <div class="gdpr-logs-page">
    <!-- Page Header Section -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <h1 class="page-title">
            {{ t('gdprLogs.title') }}
          </h1>
        </div>
      </div>
      <p class="page-description">
        {{ t('gdprLogs.summary') }}
      </p>
    </div>

    <!-- Content Section with DataTable -->
    <SpmTableAPI
      v-if="apiCall && columns.length > 0"
      :api-call="apiCall"
      :columns="columns"
      :show-reload-button="true"
      data-key="id"
      :initial-sort="{ field: 'created_at', order: 'desc' }"
    >
      <!-- Channel column -->
      <template #cell-channel="{ data }">
        <span
          class="channel-badge"
          :class="getChannelClass(data.channel)"
        >
          <i :class="getChannelIcon(data.channel)" />
          {{ getChannelLabel(data.channel) }}
        </span>
      </template>

      <!-- Source column -->
      <template #cell-source="{ data }">
        <span class="source-badge">
          {{ getSourceLabel(data.source) }}
        </span>
      </template>

      <!-- Type column -->
      <template #cell-type="{ data }">
        <span
          class="type-badge"
          :class="getTypeClass(data.type)"
        >
          {{ getTypeLabel(data.type) }}
        </span>
      </template>

      <!-- Created at column -->
      <template #cell-created_at="{ data }">
        <span class="date-display">
          {{ formatDateDisplay(data.created_at) }}
        </span>
      </template>
    </SpmTableAPI>
  </div>
</template>

<script lang="ts">
import { useI18n } from 'vue-i18n';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import type { SpmColumnConfig, ApiCallConfig } from '@/components/table-rest-api/types';
import { NestApiType } from '@/composables/nestApi';
import { defineComponent, ref } from 'vue';
import moment from 'moment';

export default defineComponent({
  name: 'GdprLogs',

  components: {
    SpmTableAPI,
  },

  setup() {
    const { t } = useI18n();

    // API configuration
    const apiCall = ref<ApiCallConfig>({
      type_api: NestApiType.CLIENT_API,
      path: '/rgpd/register',
      secure: true,
    });

    // Columns configuration
    const columns: SpmColumnConfig[] = [
      {
        id: 'first_name',
        header: t('gdprLogs.headers.firstName'),
        fields: [{
          name: 'first_name',
          label: t('gdprLogs.headers.firstName'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'last_name',
        header: t('gdprLogs.headers.lastName'),
        fields: [{
          name: 'last_name',
          label: t('gdprLogs.headers.lastName'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'email',
        header: t('gdprLogs.headers.email'),
        fields: [{
          name: 'email',
          label: t('gdprLogs.headers.email'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'phone',
        header: t('gdprLogs.headers.phone'),
        fields: [{
          name: 'phone',
          label: t('gdprLogs.headers.phone'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'channel',
        header: t('gdprLogs.headers.channel'),
        fields: [{
          name: 'channel',
          label: t('gdprLogs.headers.channel'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'singleSelect',
            options: [
              { label: t('gdprLogs.channels.email'), value: 1 },
              { label: t('gdprLogs.channels.sms'), value: 2 },
              { label: t('gdprLogs.channels.whatsapp'), value: 3 },
              { label: t('gdprLogs.channels.push'), value: 4 },
            ],
          },
        }],
      },
      {
        id: 'source',
        header: t('gdprLogs.headers.source'),
        fields: [{
          name: 'source',
          label: t('gdprLogs.headers.source'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'singleSelect',
            options: [
              { label: t('gdprLogs.sources.consent'), value: 'consent' },
              { label: t('gdprLogs.sources.reject'), value: 'reject' },
            ],
          },
        }],
      },
      {
        id: 'type',
        header: t('gdprLogs.headers.type'),
        fields: [{
          name: 'type',
          label: t('gdprLogs.headers.type'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'singleSelect',
            options: [
              { label: t('gdprLogs.types.subscribe'), value: 'subscribe' },
              { label: t('gdprLogs.types.unsubscribe'), value: 'unsubscribe' },
              { label: t('gdprLogs.types.spam_compliant'), value: 'spam_compliant' },
              { label: t('gdprLogs.types.hard_bounce'), value: 'hard_bounce' },
              { label: t('gdprLogs.types.soft_bounce'), value: 'soft_bounce' },
            ],
          },
        }],
      },
      {
        id: 'created_at',
        header: t('gdprLogs.headers.createdAt'),
        fields: [{
          name: 'created_at',
          label: t('gdprLogs.headers.createdAt'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }],
      },
    ];

    /**
     * Format date for display
     */
    const formatDateDisplay = (date: string | null): string => {
      if (!date) return '-';
      return moment(date).format('DD/MM/YYYY HH:mm');
    };

    /**
     * Get channel label
     */
    const getChannelLabel = (channel: number): string => {
      const channelMap: Record<number, string> = {
        1: t('gdprLogs.channels.email'),
        2: t('gdprLogs.channels.sms'),
        3: t('gdprLogs.channels.whatsapp'),
        4: t('gdprLogs.channels.push'),
      };
      return channelMap[channel] || '-';
    };

    /**
     * Get channel icon
     */
    const getChannelIcon = (channel: number): string => {
      const iconMap: Record<number, string> = {
        1: 'fa-solid fa-envelope',
        2: 'fa-solid fa-mobile-screen-button',
        3: 'fa-brands fa-whatsapp',
        4: 'fa-solid fa-bell',
      };
      return iconMap[channel] || 'fa-solid fa-question';
    };

    /**
     * Get channel CSS class
     */
    const getChannelClass = (channel: number): string => {
      const classMap: Record<number, string> = {
        1: 'channel-badge--email',
        2: 'channel-badge--sms',
        3: 'channel-badge--whatsapp',
        4: 'channel-badge--push',
      };
      return classMap[channel] || '';
    };

    /**
     * Get source label
     */
    const getSourceLabel = (source: string): string => {
      const normalizedSource = source?.toLowerCase() || '';
      const sourceMap: Record<string, string> = {
        consent: t('gdprLogs.sources.consent'),
        reject: t('gdprLogs.sources.reject'),
      };
      return sourceMap[normalizedSource] || source;
    };

    /**
     * Get type label
     */
    const getTypeLabel = (type: string): string => {
      const normalizedType = type?.toLowerCase() || '';
      const typeMap: Record<string, string> = {
        subscribe: t('gdprLogs.types.subscribe'),
        unsubscribe: t('gdprLogs.types.unsubscribe'),
        spam_complaint: t('gdprLogs.types.spam_compliant'),
        hard_bounce: t('gdprLogs.types.hard_bounce'),
        soft_bounce: t('gdprLogs.types.soft_bounce'),
      };
      return typeMap[normalizedType] || type;
    };

    /**
     * Get type CSS class
     */
    const getTypeClass = (type: string): string => {
      const normalizedType = type?.toLowerCase() || '';
      const classMap: Record<string, string> = {
        subscribe: 'type-badge--subscribe',
        unsubscribe: 'type-badge--unsubscribe',
        spam_complaint: 'type-badge--spam',
        hard_bounce: 'type-badge--hard-bounce',
        soft_bounce: 'type-badge--soft-bounce',
      };
      return classMap[normalizedType] || '';
    };

    return {
      t,
      apiCall,
      columns,
      formatDateDisplay,
      getChannelLabel,
      getChannelIcon,
      getChannelClass,
      getSourceLabel,
      getTypeLabel,
      getTypeClass,
    };
  },
});
</script>

<style scoped lang="scss">


.page-header {
  margin-bottom: 1.5rem;

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  .page-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
  }

  .page-description {
    color: #64748b;
    margin: 0;
  }
}

/* Channel badges */
.channel-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;

  i {
    font-size: 0.75rem;
  }

  &--email {
    background-color: #eff6ff;
    color: #1d4ed8;
  }

  &--sms {
    background-color: #f0fdf4;
    color: #15803d;
  }

  &--whatsapp {
    background-color: #ecfdf5;
    color: #059669;
  }

  &--push {
    background-color: #fef3c7;
    color: #d97706;
  }
}

/* Source badges */
.source-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: #f1f5f9;
  color: #475569;
}

/* Type badges */
.type-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;

  &--subscribe {
    background-color: #dcfce7;
    color: #166534;
  }

  &--unsubscribe {
    background-color: #fef3c7;
    color: #92400e;
  }

  &--spam {
    background-color: #fee2e2;
    color: #991b1b;
  }

  &--hard-bounce {
    background-color: #fecaca;
    color: #b91c1c;
  }

  &--soft-bounce {
    background-color: #fed7aa;
    color: #c2410c;
  }
}

/* Date display */
.date-display {
  color: #64748b;
  font-size: 0.875rem;
}
</style>
